import React from "react";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  clear_lawyer,
  create_lawyer,
  edit_lawyer,
  get_lawyer,
} from "../../../services/apis/lawyers";
import useUploadFiles from "../../../hooks/useUploadFiles";
import { ILawyer, ILawyerCreate } from "../../../store/types/lawyers";

type LawyerFormValues = Omit<
  ILawyerCreate,
  "years_experience" | "countries"
> & {
  years_experience: string;
  countries: string;
};

type LawyerFormErrors = Partial<Record<keyof LawyerFormValues, string>>;

const emptyValues: LawyerFormValues = {
  email: "",
  username: "",
  password: "",
  name: "",
  phone_number: "",
  bar_number: "",
  specialization: "",
  years_experience: "",
  bio: "",
  countries: "",
  cv_path: "",
  status: "pending",
};

const useLawyerForm = (open: boolean, lawyer: ILawyer | null) => {
  const dispatch = useAppDispatch();
  const lawyerDetails = useAppSelector(({ lawyers }) => lawyers.lawyer);
  const loading = useAppSelector(({ lawyers }) => lawyers.loading);
  const [values, setValues] = React.useState<LawyerFormValues>(emptyValues);
  const [errors, setErrors] = React.useState<LawyerFormErrors>({});
  const [cvPreview, setCvPreview] = React.useState("");
  const { loader: cvLoading, uploadFile: uploadCv } = useUploadFiles();

  React.useEffect(() => {
    if (!open) return;

    setErrors({});
    if (!lawyer) {
      clear_lawyer(dispatch);
      setValues(emptyValues);
      setCvPreview("");
      return;
    }

    clear_lawyer(dispatch);
    void get_lawyer(dispatch, String(lawyer.id));
  }, [dispatch, lawyer, open]);

  React.useEffect(() => {
    if (!open || !lawyer || lawyerDetails?.id !== lawyer.id) return;

    const application = lawyerDetails.application;
    setValues({
      email: lawyerDetails.email ?? "",
      username: lawyerDetails.username ?? "",
      password: "",
      name: lawyerDetails.name ?? "",
      phone_number: lawyerDetails.phone_number ?? "",
      bar_number: application.bar_number ?? "",
      specialization: application.specialization ?? "",
      years_experience:
        application.years_experience == null
          ? ""
          : String(application.years_experience),
      bio: application.bio ?? "",
      countries: application.countries?.join(", ") ?? "",
      cv_path: application.cv_path ?? "",
      status: application.status ?? "pending",
    });
    setCvPreview(application.cv_path ?? "");
    setErrors({});
  }, [lawyer, lawyerDetails, open]);

  const setFieldValue = (field: keyof LawyerFormValues, value: string) => {
    setValues((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: "" }));
  };

  const onChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    if (Object.prototype.hasOwnProperty.call(emptyValues, name)) {
      setFieldValue(name as keyof LawyerFormValues, value);
    }
  };

  const onCvChange = async (files: { id: string; file: File }[]) => {
    const selected = files[files.length - 1];
    if (!selected) return;

    const result = await uploadCv(selected.file, "onboarding", "document");
    if (!result.length) {
      setErrors((previous) => ({
        ...previous,
        cv_path: "CV upload failed. Please try again.",
      }));
      return;
    }

    setFieldValue("cv_path", result[0].file_path);
    setCvPreview(result[0].file_url);
  };

  const validate = () => {
    const nextErrors: LawyerFormErrors = {};
    const experience = Number(values.years_experience);
    const phoneDigits = values.phone_number.replace(/\D/g, "");

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!/^[a-zA-Z0-9_.-]{3,30}$/.test(values.username.trim())) {
      nextErrors.username = "Enter a valid username (3-30 characters).";
    }
    if (!lawyer && values.password.length < 8) {
      nextErrors.password = "Password must contain at least 8 characters.";
    }
    if (values.name.trim().length < 2) {
      nextErrors.name = "Enter a name with at least 2 characters.";
    }
    if (
      !/^\+?[0-9\s().-]+$/.test(values.phone_number.trim()) ||
      phoneDigits.length < 7 ||
      phoneDigits.length > 15
    ) {
      nextErrors.phone_number = "Enter a valid phone number.";
    }
    if (!values.bar_number.trim()) {
      nextErrors.bar_number = "Enter a bar number.";
    }
    if (!values.specialization.trim()) {
      nextErrors.specialization = "Enter a specialization.";
    }
    if (
      values.years_experience.trim() === "" ||
      !Number.isInteger(experience) ||
      experience < 0 ||
      experience > 80
    ) {
      nextErrors.years_experience =
        "Enter a whole number of years between 0 and 80.";
    }
    if (!values.bio.trim()) nextErrors.bio = "Enter a professional biography.";
    if (!values.countries.split(",").some((country) => country.trim())) {
      nextErrors.countries = "Enter at least one country.";
    }
    if (!values.cv_path) nextErrors.cv_path = "Upload a CV.";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submit = async () => {
    if (!validate()) return false;

    const payload: ILawyerCreate = {
      email: values.email.trim(),
      username: values.username.trim(),
      password: values.password,
      name: values.name.trim(),
      phone_number: values.phone_number.trim(),
      bar_number: values.bar_number.trim(),
      specialization: values.specialization.trim(),
      years_experience: Number(values.years_experience),
      bio: values.bio.trim(),
      countries: values.countries
        .split(",")
        .map((country) => country.trim())
        .filter(Boolean),
      cv_path: values.cv_path,
      status: values.status,
    };

    if (lawyer) {
      const { password, ...updatePayload } = payload;
      return edit_lawyer(
        dispatch,
        lawyer.id,
        password ? payload : updatePayload,
      );
    }
    return create_lawyer(dispatch, payload);
  };

  return {
    values,
    errors,
    cvPreview,
    loading: loading || cvLoading,
    onChange,
    setFieldValue,
    onCvChange,
    submit,
  };
};

export default useLawyerForm;
